import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";
import type { StaffItem } from "services/api/company/staff.service";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import { formatStaffPhoneNumber } from "./shared/staffPhoneNumber.logic";
import { formatPersonalIdentityNumber } from "./shared/staffPersonalIdentityNumber.logic";
import {
  AvatarCircle,
  AvatarImage,
  InactiveTag,
  PersonCell,
  PersonName,
  PersonNumber,
  PersonText,
  RowAction,
  StaffRow,
} from "../staff.styled";

interface StaffListRowProps {
  item: StaffItem;
  onSelectWorker: (workerId: number) => void;
}

const toEmploymentStatusBadgeLabel = (
  item: StaffItem,
  translate: (key: string) => string,
): string | null => {
  switch (item.status) {
    case "Active":
      return null;
    case "FutureEmployment":
      return translate("Staff.FutureEmployment");
    case "NoEmployment":
      return translate("Staff.Inactive");
    case "Inactive":
      return translate("Staff.EmploymentEnded");
  }
};

const toEmploymentStatusBadgeVariant = (
  status: StaffItem["status"],
): "default" | "future" | "ended" => {
  if (status === "FutureEmployment") return "future";
  if (status === "Inactive") return "ended";
  return "default";
};

const toDisplayName = (item: StaffItem): string => {
  const resolvedName = `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();
  return resolvedName.length > 0 ? resolvedName : String(item.workerId);
};

/**
 * Handles staff list row.
 */
export const StaffListRow = (props: StaffListRowProps): JSX.Element => {
  const { t } = useTranslation();
  const handleSelectWorker = (): void => {
    props.onSelectWorker(props.item.workerId);
  };
  const avatarUrl = useWorkerAvatar({
    workerExternalId: props.item.externalId,
    isWorker: true,
  });
  const displayName = toDisplayName(props.item);
  const employmentTypeLabel = props.item.employmentType
    ? t(`Staff.employmentType${props.item.employmentType}`)
    : "-";
  const roleLabel = (props.item.role ?? "").trim() || "-";
  const personalIdentityNumberLabel = formatPersonalIdentityNumber(
    props.item.personalIdentityNumber,
  );
  const employmentStatusBadgeLabel = toEmploymentStatusBadgeLabel(
    props.item,
    t,
  );

  return (
    <StaffRow
      type="button"
      onClick={handleSelectWorker}
      aria-label={t("Staff.ProfilePanelOpenLabel", {
        worker: displayName,
      })}
    >
      <PersonCell>
        <AvatarCircle>
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt={displayName} />
          ) : (
            <CustomIcon name="v2/person" size="20px" />
          )}
        </AvatarCircle>
        <PersonText>
          <PersonName>{displayName}</PersonName>
          <PersonNumber>{roleLabel}</PersonNumber>
        </PersonText>
      </PersonCell>
      <LabeledValue
        variant="small"
        truncate
        value={employmentTypeLabel}
        label={t("Staff.EmploymentType")}
      />
      <LabeledValue
        variant="small"
        truncate
        value={formatStaffPhoneNumber(props.item.phoneNumber)}
        label={t("Staff.PhoneNumber")}
      />
      <LabeledValue
        variant="small"
        truncate
        value={personalIdentityNumberLabel}
        label={t("Staff.AddStaffPanelFieldPersonalNumber")}
      />
      <RowAction>
        {employmentStatusBadgeLabel && (
          <InactiveTag
            $variant={toEmploymentStatusBadgeVariant(props.item.status)}
          >
            {employmentStatusBadgeLabel}
          </InactiveTag>
        )}
        <CustomIcon name="v2/right-chevron" size="18px" />
      </RowAction>
    </StaffRow>
  );
};
